import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import {
  ELSDropDown,
  ELSDropDownOption,
  ELSIcon,
  ELSRadio
} from '@els/els-component-form-field-react';
import { flowRight, orderBy } from 'lodash';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import { ELSAdobeAnalyticService } from '@els/els-ui-common-react';
import {
  putRecommendationAction,
  trackAdobeAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import {
  HesiExamRemediationSettingsDto,
  RecommendationContentTypeDto,
  RecommendationDto,
  RemediationActivityTypeDto,
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import {
  ActivityTypeConfigMap,
  RemediationContentTypeConfigMap,
  RemediationGoalType,
} from '../../models/remediation.models';
import {
  getContentTypesFromAssessmentGoal,
} from './remediation-home.utilities';
import { AssessmentGoalDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import IconWithText from '../../components/icon-with-text/IconWithText.component';
import {
  ELSButtonIconPosition,
  ELSButtonIconPrefix,
} from '../../models/button.models';
import RemediationGoalModal from './RemediationGoalModal.component';
import IsRender from '../../components/is-render/IsRender.component';
import { TotalsStatus } from '../../components/remediation-quiz/TotalsStatus.component';
import RecommendationOverview from './RecommendationOverview.component';
import RemediationCompleteBox from '../../components/remediation-quiz/RemediationCompleteBox.component';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import RecommendationCard from '../../components/recommendation-card/RecommendationCard.component';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  getAssessmentGoalTotals,
  getCategoryGroupNameForGoal,
  getContentTypeMainDisplay,
  getGoalBreadcrumbs,
  getHourAndMinuteFromSecond,
  getIncompleteRecommendations,
  getRecommendationsFromGoal,
  isGoalComplete,
  sortRecommendations
} from '../../utilities/remediation.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType,
  AdobeAnalyticsProductFeature
} from '../../models/adobe-analytics.models';
import { ViewOptionValue } from '../../constants/remediation.constants';
import { ViewOptionValueDisplayTextMap } from './remediation.constants';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import moment from 'moment';
import { ContentTypeDisplay } from '../../components/content-type-display/ContentTypeDisplay.component';

export const RemediationActivityModalId = 'RemediationCategoryModalId';

enum SkipOptionValue {
  WANT_NEXT_REC = 'WANT_NEXT_REC',
  WANT_ALL_RECS = 'WANT_ALL_RECS',
  NOT_RELEVANT = 'NOT_RELEVANT',
  ALREADY_SEEN = 'ALREADY_SEEN',
  WANT_OTHER_TYPE = 'WANT_OTHER_TYPE',
  OTHER = 'OTHER',
}

const SkipOptionConfigMap: Record<SkipOptionValue, {
  label: string;
  sortOrder: number;
  hasReason: boolean;
}> = {
  [SkipOptionValue.WANT_NEXT_REC]: {
    label: 'I want to see the next recommendation',
    sortOrder: 1,
    hasReason: false
  },
  [SkipOptionValue.WANT_ALL_RECS]: {
    label: 'I want to see all options',
    sortOrder: 2,
    hasReason: false
  },
  [SkipOptionValue.WANT_OTHER_TYPE]: {
    label: 'I want to see a different content type',
    sortOrder: 3,
    hasReason: false
  },
  [SkipOptionValue.ALREADY_SEEN]: {
    label: 'I recently completed this content in a course',
    sortOrder: 4,
    hasReason: false
  },
  [SkipOptionValue.NOT_RELEVANT]: {
    label: 'The previous recommendation was not relevant to me',
    sortOrder: 5,
    hasReason: true
  },
  [SkipOptionValue.OTHER]: {
    label: 'Other',
    sortOrder: 6,
    hasReason: true
  }
};

export type RemediationActivityModalPropsOnly = {
  assessmentGoal: AssessmentGoalDto;
}

export type RemediationActivityModalProps = RemediationActivityModalPropsOnly
  & ELSPropsFromToastService
  & ELSPropsFromModalService;

export type RemediationActivityModalState = {
  activeRecommendationId: number;
  lastActiveNotPreferredRecommendation: number;
  preferredContentType: RecommendationContentTypeDto;
  showSkipForm: boolean;
  goalRecommendations: RecommendationDto[]; // duplicate state from base state because we need to update it
  selectedRecommendation: RecommendationDto;
  selectedSkipReason: SkipOptionValue;
  selectedSkipExplanation: Partial<Record<SkipOptionValue, string>>;
}

const defaultState: RemediationActivityModalState = {
  activeRecommendationId: null,
  lastActiveNotPreferredRecommendation: null,
  showSkipForm: false,
  preferredContentType: null,
  goalRecommendations: null,
  selectedRecommendation: null,
  selectedSkipReason: null,
  selectedSkipExplanation: {},
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const RemediationActivityModalComponent = (props: RemediationActivityModalProps) => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;
  const statusCounts = getAssessmentGoalTotals({
    assessmentGoal: props.assessmentGoal,
    baseState
  });

  const getNextRecommendations = (_props: {
    recommendations: RecommendationDto[];
    activeRecommendationId: number;
    nextPreferredContentType: RecommendationContentTypeDto;
    direction: 'next' | 'prev';
  }): {
    nextId: number;
    nextSkipReason: string;
    // eslint-disable-next-line sonarjs/cognitive-complexity
  } => {

    const {
      recommendations,
      activeRecommendationId,
      nextPreferredContentType,
      direction
    } = _props;

    if (!recommendations || !recommendations.length) {
      return null;
    }

    const incompleteRecommendations = getIncompleteRecommendations(recommendations, baseState?.recommendationAttempts);
    const incompleteIds = incompleteRecommendations.map((item) => {
      return item.id
    })

    const hasIncomplete = recommendations.some((rec) => {
      return incompleteIds?.includes(rec.id);
    });

    const sorted = sortRecommendations(
      recommendations,
      baseState.eolsAssessment?.assessmentGoals
    );

    if (direction === 'prev') {
      const validRecs = sorted.filter((rec) => {
        return !hasIncomplete || (incompleteIds?.includes(rec.id));
      });
      const idx = validRecs.findIndex((rec) => {
        return rec.id === activeRecommendationId;
      });
      const nextIdx = idx - 1 < 0 ? validRecs.length - 1 : idx - 1;
      return {
        nextId: validRecs[nextIdx].id,
        nextSkipReason: validRecs[nextIdx].skipReason,
      };
    }

    if (_props.activeRecommendationId && direction === 'next') {
      const validRecs = sorted.filter((rec) => {
        return !hasIncomplete || (incompleteIds?.includes(rec.id));
      });
      const idx = validRecs.findIndex((rec) => {
        return rec.id === activeRecommendationId;
      });
      if (idx !== -1) {
        const nextIdx = idx + 1 > validRecs.length - 1 ? 0 : idx + 1;
        const nextRec = validRecs[nextIdx];
        if (nextRec?.skipReason) {
          return {
            nextId: validRecs[nextIdx].id,
            nextSkipReason: validRecs[nextIdx].skipReason,
          };
        }
      }
    }

    const isValid = (rec: RecommendationDto) => {
      return (!activeRecommendationId || activeRecommendationId !== rec.id)
        && (
          !hasIncomplete
          || (incompleteIds?.includes(rec.id))
        );
    };

    let nextRec = null;

    nextRec = sorted.find((rec) => {
      return isValid(rec)
        && rec.contentType === nextPreferredContentType
        && !rec.skipReason;
    });

    if (!nextRec) {
      // If nothing found, try again without the skip reason check
      nextRec = sorted.find((rec) => {
        return isValid(rec)
          && rec.contentType === nextPreferredContentType;
      });
    }

    if (!nextRec) {
      // If nothing found, try again without nextPreferredContentType and skip reason
      nextRec = sorted.find((rec) => {
        return isValid(rec)
          && !rec.skipReason;
      });
    }

    if (!nextRec) {
      // If nothing found, take the first available
      nextRec = sorted.find((rec) => {
        return isValid(rec);
      });
    }

    return {
      nextId: nextRec ? nextRec.id : null,
      nextSkipReason: nextRec ? nextRec.skipReason : null
    };
  };

  const [state, setState] = useState<RemediationActivityModalState>({ ...defaultState });
  const mergeState = (newState: Partial<RemediationActivityModalState>) => {
    setState((prevState) => {
      return {
        ...prevState,
        ...newState
      };
    });
  };

  const getRecommendation = (id: number) => {
    if (!state.goalRecommendations) {
      return null;
    }
    return state.goalRecommendations.find((rec) => {
      return rec.id === id;
    });
  };
  const getIsRecommendationPreferred = (recommendationId: number, contentType: RecommendationContentTypeDto) => {
    const activeRec = getRecommendation(recommendationId);
    if (!activeRec) {
      return false;
    }
    return activeRec.contentType === contentType;
  };

  const isOnlyViewAll = () => {
    return statusCounts.progress >= 1 || (state.goalRecommendations && state.goalRecommendations.length === statusCounts.activityTypeTotalGoal);
  };

  const getViewOptionType = () => {
    return isOnlyViewAll()
      ? ViewOptionValue.ALL_ACTIVITIES
      : appState.activityModalViewValue;
  };

  useEffect(() => {
    const goalRecommendations = getRecommendationsFromGoal(baseState?.recommendations, props.assessmentGoal);
    setState((prevState) => {

      const activeRecommendation = getNextRecommendations({
        recommendations: goalRecommendations,
        activeRecommendationId: null,
        nextPreferredContentType: null,
        direction: 'next'
      })

      return {
        ...prevState,
        goalRecommendations,
        activeRecommendationId: activeRecommendation?.nextId,

      };
    });
  }, []);

  const handleCancelClick = () => {
    props.modalService.closeModal(RemediationActivityModalId);
  };

  const handleSkipSubmit = () => {

    const {
      toastService
    } = props;

    if (!state.selectedSkipReason) {
      toastService.openToast({
        ...BASE_TOAST_CONFIG,
        component: <div>Please select a skip reason</div>,
        type: ELSWithToastService.types.NEGATIVE
      });
      return;
    }

    const activeRecommendation = getRecommendation(state.activeRecommendationId);
    const updatedRecommendation = {
      ...activeRecommendation,
      skipReason: state.selectedSkipReason,
    };

    if (state.selectedSkipExplanation[state.selectedSkipReason]) {
      updatedRecommendation.skipExplanation = state.selectedSkipExplanation[state.selectedSkipReason];
    }

    let preferredContentType: RecommendationContentTypeDto = null;
    if (state.selectedSkipReason === SkipOptionValue.WANT_OTHER_TYPE) {
      preferredContentType = state.selectedSkipExplanation[SkipOptionValue.WANT_OTHER_TYPE] as RecommendationContentTypeDto;
    }

    if (state.selectedSkipReason === SkipOptionValue.NOT_RELEVANT) {
      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.IRRELEVANT_RECOMMENDATION_REPORTED,
          props: {
            studentStudyEolsAssessmentId: baseState.raasAssessment?.studentStudyEolsAssessmentId,
            remediationAssignmentId: baseState.raasAssessment?.remediationAssignmentId,
            recommendationId: activeRecommendation.id,
            contentType: activeRecommendation.contentType,
            skipExplanation: state.selectedSkipExplanation[SkipOptionValue.NOT_RELEVANT]
          }
        }
      });
    }

    toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: <div>Thank you for your feedback!</div>,
      type: ELSWithToastService.types.POSITIVE
    });

    putRecommendationAction({
      setAppState,
      appState,
      appStateRef,
      recommendation: updatedRecommendation
    }).then((recommendation) => {
      setState((prevState) => {

        const updatedRecommendations = prevState.goalRecommendations.map((rec) => {
          if (rec.id === recommendation.id) {
            return { ...recommendation };
          }
          return rec;
        });

        const nextRecommendations = getNextRecommendations({
          recommendations: updatedRecommendations,
          activeRecommendationId: prevState.activeRecommendationId,
          nextPreferredContentType: preferredContentType,
          direction: 'next'
        });

        setAppState((prevAppState) => {
          return {
            ...prevAppState,
            activityModalViewValue: updatedRecommendation.skipReason === SkipOptionValue.WANT_ALL_RECS
              ? ViewOptionValue.ALL_ACTIVITIES
              : prevAppState.activityModalViewValue,
          };
        });

        return {
          ...prevState,
          activeRecommendationId: nextRecommendations.nextId,
          preferredContentType,
          showSkipForm: false,
          selectedSkipReason: null,
          selectedSkipExplanation: {},
          goalRecommendations: updatedRecommendations,
        };
      });
    });
  };

  const getAlternateContentTypesOptions = (activeRecommendation: RecommendationDto): ELSDropDownOption[] => {
    const hesiSettings = baseState.remediationAssignmentWithSources?.remediationAssignment?.settings as HesiExamRemediationSettingsDto;
    const activeActivityType: HesiExamRemediationSettingsDto['activityTypes'][0] = hesiSettings?.activityTypes?.find((activityType) => {
      return activityType.activityType === props.assessmentGoal.type;
    });

    if (!activeActivityType || !activeRecommendation) {
      return [];
    }

    const contentTypes = activeActivityType.contentSettings?.filter((contentType) => {
      return contentType.isActive && activeRecommendation.contentType !== contentType.contentType;
    });

    return contentTypes.map((contentType) => {
      return {
        name: RemediationContentTypeConfigMap[contentType.contentType].displayName,
        value: contentType.contentType,
      };
    });
  };
  const handleNextRecClick = () => {

    if (!state.activeRecommendationId) {
      return;
    }

    const activeRecommendation = getRecommendation(state.activeRecommendationId);

    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:skip:${activeRecommendation?.contentType || 'unknown'}`,
        }
      }
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.RECOMMENDATION_SKIPPED,
        props: {
          recommendationId: activeRecommendation?.id,
          contentType: activeRecommendation?.contentType,
          remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
          assessmentId: baseState.raasAssessment?.id
        }
      }
    });

    if (activeRecommendation && !activeRecommendation.skipReason) {
      const options = getAlternateContentTypesOptions(activeRecommendation);
      mergeState({
        showSkipForm: true,
        selectedSkipReason: null,
        selectedSkipExplanation: {
          [SkipOptionValue.WANT_OTHER_TYPE]: options.length > 0 ? options[0].value as string : null
        },
      });
      return;
    }

    const nextRecommendations = getNextRecommendations({
      recommendations: state.goalRecommendations,
      activeRecommendationId: state.activeRecommendationId,
      nextPreferredContentType: null,
      direction: 'next'
    });

    mergeState({
      activeRecommendationId: nextRecommendations.nextId,
      preferredContentType: null,
    });
  };

  const handlePrevRecClick = () => {
    const nextRecommendations = getNextRecommendations({
      recommendations: state.goalRecommendations,
      activeRecommendationId: state.activeRecommendationId,
      nextPreferredContentType: null,
      direction: 'prev'
    });

    mergeState({
      activeRecommendationId: nextRecommendations.nextId,
      preferredContentType: null,
    });
  };

  const handleLaunch = (selectedRecommendation: RecommendationDto) => {
    mergeState({ selectedRecommendation });
  };

  const getSkipOptions = (): ELSDropDownOption[] => {
    return orderBy(Object.keys(SkipOptionConfigMap), (key: SkipOptionValue) => {
      return SkipOptionConfigMap[key].sortOrder;
    }).map((key: SkipOptionValue) => {
      return {
        name: SkipOptionConfigMap[key].label,
        value: key,
      };
    });
  };

  const handleSkipExplanationChange = (skipReason: SkipOptionValue, explanation: string) => {
    setState((prevState) => {
      return {
        ...prevState,
        selectedSkipExplanation: {
          ...prevState.selectedSkipExplanation,
          [skipReason]: explanation
        }
      };
    });
  };

  const handleViewOptionValueSetting = (value: ViewOptionValue) => {
    if (value === appState.activityModalViewValue) {
      return;
    }

    ELSAdobeAnalyticService.trackEvent(AdobeAnalyticsAction.PRODUCT_FEATURE_USED, {
      feature: {
        name: AdobeAnalyticsProductFeature.ACTIVITY_MODAL_LIST_VIEW_BTN_TOGGLE_CLICK
      }
    });

    const isOneAtATimeSelected = value === ViewOptionValue.ONE_AT_A_TIME;

    // Track specific button clicks for View All vs Show Most Relevant
    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:${isOneAtATimeSelected ? 'showMostRelevant' : 'viewAllRecommendations'}`,
        }
      }
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.RECOMMENDATION_VIEW_TOGGLED,
        props: {
          viewType: isOneAtATimeSelected ? 'showMostRelevant' : 'showAll',
          remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
          assessmentId: baseState.raasAssessment?.id
        }
      }
    });

    setState((prevState) => {
      return {
        ...prevState,
        showSkipForm: false,
      };
    });
    setAppState((prevAppState) => {
      return {
        ...prevAppState,
        activityModalViewValue: value
      };
    });
  };

  const getIsPrevRecDisabled = (): boolean => {

    const nextRecommendation = getNextRecommendations({
      recommendations: state.goalRecommendations,
      activeRecommendationId: state.activeRecommendationId,
      nextPreferredContentType: null,
      direction: 'prev'
    });

    return !nextRecommendation
      || !nextRecommendation.nextId
      || !nextRecommendation.nextSkipReason;
  };

  const showPreferredError = () => {
    return state.preferredContentType
      && !getIsRecommendationPreferred(state.activeRecommendationId, state.preferredContentType);
  };

  const contentTypes = getContentTypesFromAssessmentGoal(props.assessmentGoal, baseState);
  const { goalType } = ActivityTypeConfigMap[props.assessmentGoal.type];
  const activeRecommendation = getRecommendation(state.activeRecommendationId);
  const alternativeContentTypeOptions = getAlternateContentTypesOptions(activeRecommendation);
  const isPrevRecDisabled = getIsPrevRecDisabled();
  const goalBreadcrumbs = getGoalBreadcrumbs(props.assessmentGoal, getCategoryGroupNameForGoal(props.assessmentGoal, baseState));

  return (
    <RemediationGoalModal
      handleCancelClick={handleCancelClick}
      assessmentGoal={props.assessmentGoal}>

      {
        isGoalComplete(statusCounts) && !state.selectedRecommendation && (
          <div className="u-els-margin-bottom-1x">
            <RemediationCompleteBox activityType={props.assessmentGoal.type as RemediationActivityTypeDto}/>
          </div>
        )
      }

      {
        state.selectedRecommendation && (
          <RecommendationOverview
            recommendation={state.selectedRecommendation}
            handleReturnNavigation={
              () => {
                mergeState({ selectedRecommendation: null });
              }
            }
          />
        )
      }

      {
        !state.selectedRecommendation && (
          <div>
            <table className="c-ssa-remediation-goal-modal__goal-table">
              <tbody>

              {
                goalBreadcrumbs.path && goalBreadcrumbs.path.length > 0 && (
                  <tr>
                    <td className="u-els-nowrap">
                      <IconWithText iconName="bullseye" iconPrefix="gizmo">Subject Area</IconWithText>
                    </td>
                    <td>
                      {goalBreadcrumbs.pathDisplay}
                    </td>
                  </tr>
                )
              }

              <tr>
                <td>
                  <IconWithText iconName="syllabus" iconPrefix="hmds">Instructions</IconWithText>
                </td>
                <td>
                  <IsRender isRender={goalType === RemediationGoalType.TAXON}>
                    Review <strong>{props.assessmentGoal.goal} {getContentTypeMainDisplay(contentTypes, props.assessmentGoal.goal)}</strong> for
                    this topic.
                    We have retrieved relevant review content based on the questions you have missed in this topic.
                  </IsRender>
                  <IsRender isRender={goalType === RemediationGoalType.CONTENT_TYPE}>
                    Complete <strong>{props.assessmentGoal.goal} {getContentTypeMainDisplay(contentTypes, props.assessmentGoal.goal)}</strong> to
                    complete requirements for this section.
                  </IsRender>
                </td>
              </tr>
              <tr>
                <td>
                  <IconWithText iconName="statistics" iconPrefix="gizmo">Progress</IconWithText>
                </td>
                <td>
                  <FlexLayout modifiers={[
                    FlexLayoutModifier.LEFT,
                    FlexLayoutModifier.GUTTERS_1o2,
                    FlexLayoutModifier.MIDDLE
                  ]}>
                    <FlexItem>
                      <TotalsStatus
                        completedGoal={props.assessmentGoal.goal}
                        completed={statusCounts.activityTypeTotalGoalCompleted}
                        total={statusCounts.activityTypeTotalGoal}
                      />
                    </FlexItem>
                    <FlexItem>
                      <IsRender isRender={goalType === RemediationGoalType.TAXON}>
                        {getContentTypeMainDisplay(contentTypes, null)} completed in this topic
                      </IsRender>

                      <IsRender isRender={goalType === RemediationGoalType.CONTENT_TYPE}>
                        {getContentTypeMainDisplay(contentTypes, null)} requirements met
                      </IsRender>
                    </FlexItem>
                  </FlexLayout>
                </td>
              </tr>
              </tbody>
            </table>

            <div className="o-els-container c-els-divider u-els-color-n0"/>

            {
              !getViewOptionType() && (
                <div className="u-els-padding">
                  <div className="u-els-margin-bottom">
                    <div className="u-els-font-size-h3">
                      How do you want to see
                      your {ActivityTypeConfigMap[props.assessmentGoal.type].title.toLowerCase()} activities?
                    </div>
                    <div>
                      We’ll only ask you this once. You can change your view anytime using the “Viewing” dropdown in the top
                      left.
                    </div>
                  </div>
                  <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.WRAP_AT_MOBILE]}>
                    <FlexItem modifiers={[FlexLayoutModifier.GROW]}
                              classes={['c-ssa-remediation-goal-modal__view-option-item']}>
                      <button
                        className="c-els-button--debuttonize c-ssa-recommendation-card"
                        onClick={() => handleViewOptionValueSetting(ViewOptionValue.ONE_AT_A_TIME)}
                      >
                        <div className="c-ssa-remediation-goal-modal__view-option-card">
                          <div className="u-els-padding">
                            <ELSIcon
                              prefix={ELSButtonIconPrefix.GIZMO}
                              name="star"
                              size="2x"
                            />
                          </div>
                          <div>
                            <div
                              className="u-els-font-size-h2 u-els-margin-bottom-1o4">View {ViewOptionValueDisplayTextMap[ViewOptionValue.ONE_AT_A_TIME].toLowerCase()}</div>
                            <div>
                              See your most relevant activity based on your missed questions in this topic.
                              <br/>
                              Skip to another activity at any time.
                            </div>
                          </div>
                        </div>
                      </button>
                    </FlexItem>
                    <FlexItem modifiers={[FlexLayoutModifier.GROW]}
                              classes={['c-ssa-remediation-goal-modal__view-option-item']}>
                      <button
                        className="c-els-button--debuttonize c-ssa-recommendation-card"
                        onClick={() => handleViewOptionValueSetting(ViewOptionValue.ALL_ACTIVITIES)}
                      >
                        <div className="c-ssa-remediation-goal-modal__view-option-card">
                          <div className="u-els-padding">
                            <ELSIcon
                              prefix={ELSButtonIconPrefix.GIZMO}
                              name="list"
                              size="2x"
                            />
                          </div>
                          <div>
                            <div
                              className="u-els-font-size-h2 u-els-margin-bottom-1o4">View {ViewOptionValueDisplayTextMap[ViewOptionValue.ALL_ACTIVITIES].toLowerCase()}</div>
                            <div>
                              See your full list of relevant activities.
                              <br/>
                              Select what to work on.
                            </div>
                          </div>
                        </div>
                      </button>
                    </FlexItem>
                  </FlexLayout>
                </div>
              )
            }

            {
              !isOnlyViewAll() && getViewOptionType() !== null && (
                <div className="o-els-container">
                  <div className="c-ssa-remediation-goal-modal__view-option-toggle">
                    <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.LEFT]}>
                      <FlexItem>
                      <span className="u-els-font-size-h4 u-els-margin-right">
                        Viewing:
                      </span>
                      </FlexItem>
                      <FlexItem>
                        <ELSDropDown
                          value={appState.activityModalViewValue}
                          options={[
                            {
                              value: ViewOptionValue.ONE_AT_A_TIME,
                              name: ViewOptionValueDisplayTextMap[ViewOptionValue.ONE_AT_A_TIME]
                            },
                            {
                              value: ViewOptionValue.ALL_ACTIVITIES,
                              name: ViewOptionValueDisplayTextMap[ViewOptionValue.ALL_ACTIVITIES]
                            }
                          ]}
                          changeHandler={(e, value) => handleViewOptionValueSetting(value)}
                        />
                      </FlexItem>
                    </FlexLayout>
                  </div>
                </div>
              )
            }

            {
              state.showSkipForm && getViewOptionType() === ViewOptionValue.ONE_AT_A_TIME && (
                <div className="c-ssa-remediation-goal-modal__card-wrap">
                  <div className="c-ssa-remediation-goal-modal__skip-form">
                    <div className="c-ssa-remediation-goal-modal__skip-form-padding">
                      <FlexLayout modifiers={[FlexLayoutModifier.SPACE_BETWEEN]}>
                        <FlexItem>
                          <strong>{activeRecommendation.title}</strong>
                        </FlexItem>
                        <FlexItem>
                          <FlexLayout>
                            <FlexItem>
                              <ContentTypeDisplay contentType={activeRecommendation.contentType}/>
                            </FlexItem>
                            <FlexItem>
                              <span>&nbsp;&bull;&nbsp;</span>
                            </FlexItem>
                            <FlexItem>
                              <span className="u-els-color-n8">
                                est. {getHourAndMinuteFromSecond(moment.duration(activeRecommendation.estimatedTime).asSeconds())}
                              </span>
                            </FlexItem>
                          </FlexLayout>
                        </FlexItem>
                      </FlexLayout>
                    </div>
                    <div className="c-els-divider--1o2 c-els-divider--n3"/>
                    <div className="c-ssa-remediation-goal-modal__skip-form-padding">
                      <div className="o-els-container">Why did you skip the previous recommendation?</div>
                      {getSkipOptions().map((option) => {
                        const isChecked = state.selectedSkipReason === option.value;
                        return (
                          <div
                            className="o-els-container"
                            key={option.value}
                          >
                            <ELSRadio
                              changeHandler={(e, value) => {
                                setState((prevState) => {
                                  return {
                                    ...prevState,
                                    selectedSkipReason: value,
                                  };
                                });
                              }}
                              name={option.value as string}
                              value={option.value}
                              checked={isChecked}
                            >
                              {option.name}
                            </ELSRadio>

                            {
                              isChecked
                              && option.value === SkipOptionValue.WANT_OTHER_TYPE
                              && alternativeContentTypeOptions.length > 0
                              && (
                                <div className="o-els-container u-els-margin-left-2x">
                                  <ELSDropDown
                                    value={activeRecommendation.skipExplanation}
                                    options={alternativeContentTypeOptions}
                                    changeHandler={(e, value) => {
                                      handleSkipExplanationChange(option.value as SkipOptionValue, value);
                                    }}
                                  >
                                    Preferred content type:
                                  </ELSDropDown>
                                </div>
                              )
                            }

                            {
                              isChecked && SkipOptionConfigMap[option.value].hasReason && (
                                <div className="o-els-container u-els-margin-left-2x">
                                  <div className="c-els-field">
                                  <textarea
                                    value={state.selectedSkipExplanation[option.value] || ''}
                                    className="c-els-field__input"
                                    placeholder="Please explain..."
                                    onChange={(e) => {
                                      handleSkipExplanationChange(option.value as SkipOptionValue, e.target.value);
                                    }}
                                  />
                                  </div>
                                </div>
                              )
                            }
                          </div>
                        );
                      })}

                      <div className="u-els-margin-top">
                        <FlexLayout modifiers={[FlexLayoutModifier.RIGHT]}>
                          <FlexItem>
                            <button
                              type="button"
                              className="c-els-button c-els-button--small"
                              onClick={() => {
                                handleSkipSubmit();
                              }}
                            >
                              Submit
                            </button>
                          </FlexItem>
                        </FlexLayout>
                      </div>
                    </div>
                  </div>

                </div>
              )
            }

            {
              !state.showSkipForm
              && !showPreferredError()
              && getViewOptionType() === ViewOptionValue.ONE_AT_A_TIME
              && (
                <div className="c-ssa-remediation-goal-modal__card-wrap">

                  {activeRecommendation && (
                    <div>
                      <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE]}>
                        <FlexItem>
                          {
                            isPrevRecDisabled && (
                              <ELSIcon
                                size="1x"
                                prefix="hmds"
                                name="chevron-left"
                                customClass="u-els-color-n3"
                              />
                            )
                          }
                          {
                            !isPrevRecDisabled && (
                              <button
                                type="button"
                                className="u-els-anchorize"
                                onClick={handlePrevRecClick}
                              >
                                <ELSIcon
                                  size="1x"
                                  prefix="hmds"
                                  name="chevron-left"
                                />
                              </button>
                            )
                          }

                        </FlexItem>
                        <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                          <RecommendationCard
                            recommendation={activeRecommendation}
                            recommendationAttempts={baseState?.recommendationAttempts}
                            isExpanded
                            handleLaunch={handleLaunch}
                            launchButtonText="View details"
                            banner={null}
                          />
                        </FlexItem>
                        <FlexItem>
                          <button
                            type="button"
                            className="u-els-anchorize"
                            onClick={handleNextRecClick}
                          >
                            <ELSIcon
                              size="1x"
                              prefix="hmds"
                              name="chevron-right"
                            />
                          </button>
                        </FlexItem>
                      </FlexLayout>

                    </div>
                  )}

                </div>
              )
            }

            {
              showPreferredError()
              && getViewOptionType() === ViewOptionValue.ONE_AT_A_TIME
              && (
                <div className="c-ssa-remediation-goal-modal__card-wrap">
                  <div className="c-ssa-remediation-goal-modal__skip-form c-ssa-remediation-goal-modal__skip-form--warning">
                    We’re sorry, we do not have any relevant&nbsp;
                    {RemediationContentTypeConfigMap[state.preferredContentType].titlePlural.toLowerCase()}
                    &nbsp;in this topic.
                    <div className="u-els-margin-top">
                      <FlexLayout>
                        <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                          {
                            !isPrevRecDisabled && (
                              <button
                                type="button"
                                className="u-els-anchorize"
                                onClick={handlePrevRecClick}
                              >
                                <IconWithText
                                  iconName="arrow-left"
                                  iconPrefix="gizmo"
                                  iconPosition={ELSButtonIconPosition.LEFT}
                                >
                                  Previous recommendation
                                </IconWithText>
                              </button>
                            )
                          }
                        </FlexItem>
                        <FlexItem>
                          <button
                            type="button"
                            className="u-els-anchorize"
                            onClick={() => {
                              mergeState({
                                preferredContentType: null
                              });
                            }}
                          >
                            <IconWithText
                              iconName="arrow-right"
                              iconPrefix="gizmo"
                              iconPosition={ELSButtonIconPosition.RIGHT}
                            >
                              Next recommendation
                            </IconWithText>
                          </button>
                        </FlexItem>
                      </FlexLayout>
                    </div>
                  </div>
                </div>
              )
            }

            {
              getViewOptionType() === ViewOptionValue.ALL_ACTIVITIES && (
                <div>
                  <div className="c-ssa-remediation-goal-modal__list">
                    {state.goalRecommendations && sortRecommendations(state.goalRecommendations, baseState.eolsAssessment?.assessmentGoals)
                      .map((recommendation) => {
                        return (
                          <div
                            key={recommendation.id}
                            className="c-ssa-remediation-goal-modal__list-item"
                          >
                            <RecommendationCard
                              recommendationAttempts={baseState?.recommendationAttempts}
                              recommendation={recommendation}
                              isExpanded
                              handleLaunch={handleLaunch}
                              launchButtonText="View details"
                              banner={null}
                            />
                          </div>
                        );
                      })}
                  </div>
                </div>
              )
            }

          </div>
        )
      }

    </RemediationGoalModal>
  );
};

const enhance = flowRight(
  ELSWithModalService,
  ELSWithToastService,
);

const RemediationActivityModal = enhance(RemediationActivityModalComponent as unknown as React.FunctionComponent<unknown>) as unknown as React.FC<RemediationActivityModalPropsOnly>;

export default RemediationActivityModal;
